import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';
import axios from 'axios';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Input from '@/components/ui/Input';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { LEAD_COORDINATORS_TO_REVIEW_URL, LeadCoordinatorReviewDtoIn, LeadCoordinatorToReviewDtoOut } from '@/models/out/LeadCoordinatorToReviewDtoOut';
import settingsStyles from '@/pages/FundSettings/FundSettings.module.css';
import peopleStyles from '@/pages/FundSettings/people/People.module.css';
import LeadCoordinatorReviewBlade from './LeadCoordinatorReviewBlade';
import { CoordinatorToReview, byCoordinator, fundLabel, fundsText, nameOf, recordNameOf, requestFor } from './reviewRows';

const PREFIX = 'leadCoordinatorsReview';

/**
 * The coordinator and record cells are two lines: the name with the email beside it, then
 * the ID or where the record appears. The board is told so (`twoLineRows`); without it the
 * rows sit at a one-line pitch and each cell spilled over the buttons of the row below
 * (SPR-10552). Every line stays one line - a long email or list of projects is cut with an
 * ellipsis and shown in full on hover, rather than wrapping into a line the row has no room for.
 */
const STACK = 'flex flex-col min-w-0';
const LINE = 'block truncate';
/** Name and email share the first line; the email gives way first when the column is narrow. */
const NAME_LINE = 'flex items-baseline gap-2 min-w-0';
const NAME = 'body-md-semibold truncate shrink-0 max-w-[60%]';
const BESIDE = 'body-sm-regular truncate min-w-0';

type Outcome = { stillWaiting: boolean };

/** The search box finds a coordinator by any of their projects, by them, or by the record - name, email or number. */
const searchRow: FilterFn<CoordinatorToReview> = (row, _columnId, query: string) => {
  const r = row.original;
  const text = [...r.funds.map(fundLabel), nameOf(r), r.email, r.israeliId, recordNameOf(r), r.recordEmail].join(' ').toLowerCase();
  return text.includes(
    String(query ?? '')
      .trim()
      .toLowerCase()
  );
};

/** The answers come back as plain Hebrew sentences; anything else gets the generic one. */
const serverText = (err: unknown, fallback: string) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim() ? err.response.data : fallback;

/** «מספר זהות אחר»: the office types the coordinator's correct number; the role is tried again at once. */
const CorrectIdDialog = ({ row, onClose, onDone }: { row: CoordinatorToReview; onClose: () => void; onDone: (outcome: Outcome) => void }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const { mutate: save, isPending } = usePostAuthApi<Outcome, LeadCoordinatorReviewDtoIn>(`${LEAD_COORDINATORS_TO_REVIEW_URL}/israeli-id`);

  const submit = () => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return setFieldError(t(`${PREFIX}.idRequired`));
    if (!isValidIsraeliID(digits)) return setFieldError(t(`${PREFIX}.idInvalid`));
    setFieldError(null);
    save(
      { ...requestFor(row), israeliId: digits.padStart(9, '0') },
      { onSuccess: onDone, onError: (err) => setFieldError(serverText(err, t(`${PREFIX}.errorSaving`))) }
    );
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(open: boolean) => !open && !isPending && onClose()}
      title={t(`${PREFIX}.idTitle`, { name: nameOf(row) })}
      maxWidth="520px"
      fitContent
    >
      <div className={peopleStyles.form}>
        <p className={peopleStyles.dialogNote}>{t(`${PREFIX}.idIntro`)}</p>
        <div className={settingsStyles.formField}>
          <label className={settingsStyles.fieldLabel} htmlFor="lead-review-israeli-id">
            {t(`${PREFIX}.idField`)}
          </label>
          <Input
            id="lead-review-israeli-id"
            className={`w-full ${peopleStyles.idInput}`}
            inputMode="numeric"
            dir="ltr"
            maxLength={9}
            value={value}
            onChange={(e) => {
              setValue(e.target.value.replace(/\D/g, ''));
              setFieldError(null);
            }}
            error={!!fieldError}
          />
          {fieldError && <p className={settingsStyles.formError}>{fieldError}</p>}
        </div>
        <div className={peopleStyles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending}>
            {isPending ? t('loading') : t(`${PREFIX}.idSave`)}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t(`${PREFIX}.cancel`)}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

/**
 * «זה אותו אדם»: the record takes the coordinator's email and the lead role takes effect. When the
 * record already had another email, the dialog says that one is replaced.
 */
const LinkDialog = ({ row, onClose, onDone }: { row: CoordinatorToReview; onClose: () => void; onDone: (outcome: Outcome) => void }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { mutate: link, isPending } = usePostAuthApi<Outcome, LeadCoordinatorReviewDtoIn>(`${LEAD_COORDINATORS_TO_REVIEW_URL}/link`);

  const replaces = !!row.recordEmail && row.recordEmail.trim().toLowerCase() !== row.email.trim().toLowerCase();
  const message = [
    t(`${PREFIX}.linkMessage`, { count: row.funds.length, record: recordNameOf(row), email: row.email, name: nameOf(row), funds: fundsText(row) }),
    replaces && t(`${PREFIX}.linkReplacesEmail`, { email: row.recordEmail }),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      title={t(`${PREFIX}.linkTitle`, { name: nameOf(row) })}
      message={message}
      confirmText={isPending ? 'saving' : `${PREFIX}.linkConfirm`}
      cancelText={`${PREFIX}.cancel`}
      onConfirm={() => {
        setError(null);
        link(requestFor(row), { onSuccess: onDone, onError: (err) => setError(serverText(err, t(`${PREFIX}.errorSaving`))) });
      }}
      onCancel={onClose}
      isLoading={isPending}
      error={error}
    />
  );
};

/**
 * «לא למנות לרכז ראשי» (3.11.2026): the appointment is taken back in every project where it waits; the
 * person stays a coordinator. A project with no other lead coordinator is left without one.
 */
const CancelDialog = ({ row, onClose, onDone }: { row: CoordinatorToReview; onClose: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { mutate: cancel, isPending } = usePostAuthApi<{ funds: number[] }, LeadCoordinatorReviewDtoIn>(`${LEAD_COORDINATORS_TO_REVIEW_URL}/cancel`);

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      title={t(`${PREFIX}.cancelTitle`, { name: nameOf(row) })}
      message={t(`${PREFIX}.cancelMessage`, { count: row.funds.length, name: nameOf(row), funds: fundsText(row) })}
      confirmText={isPending ? 'saving' : `${PREFIX}.cancelConfirm`}
      cancelText={`${PREFIX}.cancel`}
      onConfirm={() => {
        setError(null);
        cancel(requestFor(row), { onSuccess: onDone, onError: (err) => setError(serverText(err, t(`${PREFIX}.errorSaving`))) });
      }}
      onCancel={onClose}
      isLoading={isPending}
      error={error}
    />
  );
};

/**
 * «רכזים ראשיים לבדיקה» (29.10.2026): lead coordinators held back because the Israeli ID on their
 * account is already on a record under another name - nearly always the same person as a supported,
 *  * sometimes someone else's number. The office says which: «זה אותו אדם» links the record, «מספר זהות
 * אחר» corrects the account. Either way the role is tried again at once, and a row that is settled
 * leaves the list. One row per coordinator, by name; clicking it opens the coordinator's card with all
 * four ways to settle it (HAT-10552).
 */
const LeadCoordinatorsReviewPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [linkRow, setLinkRow] = useState<CoordinatorToReview | null>(null);
  const [idRow, setIdRow] = useState<CoordinatorToReview | null>(null);
  const [cancelRow, setCancelRow] = useState<CoordinatorToReview | null>(null);
  const [openRow, setOpenRow] = useState<CoordinatorToReview | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'info'; text: string } | null>(null);

  const { data: rows, isLoading, isError, refetch } = useGetAuthApi<LeadCoordinatorToReviewDtoOut[]>(LEAD_COORDINATORS_TO_REVIEW_URL);
  const coordinators = useMemo(() => byCoordinator(rows ?? []), [rows]);

  const settled = (text: { type: 'success' | 'info'; text: string }) => {
    setLinkRow(null);
    setIdRow(null);
    setCancelRow(null);
    setOpenRow(null);
    setNotice(text);
    void refetch();
  };

  const done = (row: CoordinatorToReview) => (outcome: Outcome) =>
    settled(
      outcome.stillWaiting
        ? { type: 'info', text: t(`${PREFIX}.doneStillWaiting`, { name: nameOf(row) }) }
        : { type: 'success', text: t(`${PREFIX}.doneActive`, { count: row.funds.length, name: nameOf(row), funds: fundsText(row) }) }
    );

  const open = (pick: (row: CoordinatorToReview) => void) => (row: CoordinatorToReview) => {
    setNotice(null);
    pick(row);
  };

  const recordWhere = (row: CoordinatorToReview) =>
    [
      row.recordSupportedInFunds.length > 0 &&
        t(`${PREFIX}.supportedIn`, { count: row.recordSupportedInFunds.length, funds: row.recordSupportedInFunds.join(', ') }),
      row.recordRepresentsFunds.length > 0 &&
        t(`${PREFIX}.representsIn`, { count: row.recordRepresentsFunds.length, funds: row.recordRepresentsFunds.join(', ') }),
    ]
      .filter(Boolean)
      .join(' · ');

  const columns: ColumnDef<CoordinatorToReview>[] = [
    {
      id: 'coordinator',
      header: Col(t(`${PREFIX}.coordinator`)),
      accessorFn: (row) => nameOf(row),
      sortingFn: (a, b) => nameOf(a.original).localeCompare(nameOf(b.original), 'he'),
      cell: ({ row }) => (
        <span className={STACK}>
          <span className={NAME_LINE}>
            <span className={NAME} title={nameOf(row.original)}>
              {nameOf(row.original)}
            </span>
            <bdi dir="ltr" className={`${BESIDE} text-(--font-body-2)`} title={row.original.email}>
              {row.original.email}
            </bdi>
          </span>
          {row.original.israeliId && (
            <span className={`body-sm-regular text-(--font-body-2) ${LINE}`}>{t(`${PREFIX}.idLabel`, { id: row.original.israeliId })}</span>
          )}
        </span>
      ),
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'record',
      header: Col(t(`${PREFIX}.record`)),
      accessorFn: (row) => `${recordNameOf(row)} ${row.recordEmail ?? ''}`,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.recordContactId == null ? (
          <span className="text-(--font-body-3)">{t(`${PREFIX}.noRecord`)}</span>
        ) : (
          <span className={STACK}>
            <span className={NAME_LINE}>
              <span className={NAME} title={recordNameOf(row.original)}>
                {recordNameOf(row.original)}
              </span>
              {row.original.recordEmail ? (
                <bdi dir="ltr" className={`${BESIDE} text-(--font-body-2)`} title={row.original.recordEmail}>
                  {row.original.recordEmail}
                </bdi>
              ) : (
                <span className={`${BESIDE} text-(--font-body-3)`}>{t(`${PREFIX}.noEmail`)}</span>
              )}
            </span>
            {recordWhere(row.original) && (
              <span className={`body-sm-regular text-(--font-body-2) ${LINE}`} title={recordWhere(row.original)}>
                {recordWhere(row.original)}
              </span>
            )}
          </span>
        ),
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'funds',
      header: Col(t(`${PREFIX}.funds`)),
      accessorFn: (row) => row.funds.map(fundLabel).join(' '),
      enableSorting: false,
      cell: ({ row }) => {
        const [first, ...rest] = row.original.funds;
        const all = row.original.funds.map(fundLabel).join(' · ');
        return (
          <span className={STACK} title={all}>
            <span className={`body-md-regular ${LINE}`}>{fundLabel(first)}</span>
            {rest.length > 0 && (
              <span className={`body-sm-regular text-(--font-body-2) ${LINE}`}>
                {t(`${PREFIX}.moreFunds`, { funds: rest.map((f) => f.fundId).join(', ') })}
              </span>
            )}
          </span>
        );
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'actions',
      header: Col(t(`${PREFIX}.actions`)),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex gap-2 [&>button]:shrink-0">
          {row.original.recordContactId != null && (
            <Button
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                open(setLinkRow)(row.original);
              }}
            >
              {t(`${PREFIX}.same`)}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              open(setOpenRow)(row.original);
            }}
          >
            {t(`${PREFIX}.moreActions`)}
          </Button>
        </span>
      ),
      meta: { className: 'min-w-[260px]', lockLayout: true },
    },
  ];

  return (
    <div>
      {isError && <Alert type="error" message={t(`${PREFIX}.errorLoading`)} />}
      {notice && <Alert type={notice.type} message={notice.text} onClose={() => setNotice(null)} />}
      <DataTable
        title={t(`${PREFIX}.title`)}
        titleCaption={<span className="body-md-regular text-(--font-body-2)">{t(`${PREFIX}.intro`)}</span>}
        data={coordinators}
        isLoading={isLoading}
        columns={columns}
        getRowId={(row) => row.email.trim().toLowerCase()}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={searchRow}
        twoLineRows
        handleRowClick={open(setOpenRow)}
        emptyStateContent={{ title: t(`${PREFIX}.emptyTitle`), description: t(`${PREFIX}.emptyText`) }}
      />
      {openRow && (
        <LeadCoordinatorReviewBlade
          key={openRow.email}
          row={openRow}
          onClose={() => setOpenRow(null)}
          onSame={setLinkRow}
          onOtherId={setIdRow}
          onCancel={setCancelRow}
        />
      )}
      {linkRow && <LinkDialog key={linkRow.email} row={linkRow} onClose={() => setLinkRow(null)} onDone={done(linkRow)} />}
      {idRow && <CorrectIdDialog key={idRow.email} row={idRow} onClose={() => setIdRow(null)} onDone={done(idRow)} />}
      {cancelRow && (
        <CancelDialog
          key={cancelRow.email}
          row={cancelRow}
          onClose={() => setCancelRow(null)}
          onDone={() =>
            settled({
              type: 'success',
              text: t(`${PREFIX}.cancelled`, { count: cancelRow.funds.length, name: nameOf(cancelRow), funds: fundsText(cancelRow) }),
            })
          }
        />
      )}
    </div>
  );
};

export default LeadCoordinatorsReviewPage;
